import { IS_CLOUD } from '@qality/common-config';
import { ReportFetchResult } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

const REPORT_INDEXES = {
  TEST_CASE: 0,
  TEST_EXECUTIONS: 1,
};

const REPORT_WITH_REQUIREMENTS_INDEXES = {
  REQUIREMENT: 0,
  TEST_CASE: 1,
  TEST_EXECUTIONS: 2,
};

export default {
  async getData(
    pageNumber: number,
    filters?: { [key: string]: string }
  ): Promise<ReportFetchResult> {
    const cleanFilters: { [key: string]: string } =
      Utils.removeEmptyStringValuesFromMap(filters);

    const isRequirementView = !Utils.isNilOrEmpty(
      cleanFilters?.requirementTypeName
    );

    const rawData = await getTraceabilityData(pageNumber, filters);
    const adjustedData = isRequirementView
      ? adjustDataWithRequirementsToFrontend(rawData)
      : adjustDataToFrontend(rawData);
    return convertToReportFetchResult(adjustedData);
  },
};

function adjustDataToFrontend(data) {
  if (data.rows.content.length) {
    data.rows.content = data.rows.content.map((row) => {
      row[REPORT_INDEXES.TEST_CASE] = adjustIssueToReport(
        row[REPORT_INDEXES.TEST_CASE]
      );
      row[REPORT_INDEXES.TEST_EXECUTIONS] = adjustTestExecutionsToReport(
        row[REPORT_INDEXES.TEST_EXECUTIONS],
        row[REPORT_INDEXES.TEST_CASE]?.key
      );
      return row;
    });
  }
  return data;
}

function adjustDataWithRequirementsToFrontend(data) {
  const flattenedData = flatData(data);
  if (flattenedData.rows.content.length) {
    flattenedData.rows.content = flattenedData.rows.content.map((row) => {
      row[REPORT_WITH_REQUIREMENTS_INDEXES.REQUIREMENT] = adjustIssueToReport(
        row[REPORT_WITH_REQUIREMENTS_INDEXES.REQUIREMENT]
      );
      row[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_CASE] = adjustIssueToReport(
        row[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_CASE]
      );
      row[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_EXECUTIONS] =
        adjustTestExecutionsToReport(
          row[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_EXECUTIONS],
          row[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_CASE]?.key
        );
      return row;
    });
  }
  return flattenedData;
}

function flatData(data) {
  const flattenedData = {
    columnKeys: [data.columnKeys[0], ...data.columnKeys[1].columnKeys],
    rows: {
      content: [],
      pageNumber: data.rows.pageNumber,
      pageSize: data.rows.pageSize,
      totalElements: data.rows.totalElements,
      totalPages: data.rows.totalPages,
    },
  };
  data.rows.content.forEach((rows) => {
    if (Utils.isNilOrEmpty(rows[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_CASE])) {
      flattenedData.rows.content.push([rows[0], null, null, []]);
    }
    rows[REPORT_WITH_REQUIREMENTS_INDEXES.TEST_CASE].forEach(
      (tableCell, testCaseIndex) => {
        flattenedData.rows.content.push([
          testCaseIndex === 0
            ? rows[0]
            : {
                summary: '',
              },
          tableCell[0].value,
          tableCell[1].value,
          tableCell[2].value,
        ]);
      }
    );
  });

  return flattenedData;
}

function convertToReportFetchResult(data) {
  const report = new ReportFetchResult(
    {
      currentPage: data.rows.pageNumber,
      pageSize: data.rows.pageSize,
      totalElements: data.rows.totalElements,
      totalPages: data.rows.totalPages,
    },
    data
  );
  return report;
}
function removeEmptyDates(filters) {
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (key === 'startDate' || key === 'endDate') {
        if (filters[key] === 'none') {
          delete filters['startDate'];
          delete filters['endDate'];
        }
      }
    });
  }
}

async function getTraceabilityData(
  pageNumber: number,
  filters?: { [key: string]: string }
) {
  removeEmptyDates(filters);

  const AuthenticateService = StorageOfUniqueServices.get(
    'AuthenticateService'
  );
  const WindowOriginService = StorageOfUniqueServices.get(
    'WindowOriginService'
  );
  const APIConstants = StorageOfUniqueServices.get('APIConstants');

  const url = await AuthenticateService.authenticateByJWT(
    WindowOriginService.origin + APIConstants.URL_TRACEABILITY_REPORT,
    convertToString(pageNumber, filters)
  );

  return (await axios.get(encodeURI(url))).data;
}

function adjustIssueToReport(issue) {
  return issue
    ? {
        ...issue,
        icon: IS_CLOUD
          ? issue.fields?.issuetype?.iconUrl
          : issue.issuetype?.iconUrl,
      }
    : null;
}

function adjustTestExecutionsToReport(testExecutions, testCaseKey) {
  return testExecutions
    ? {
        ...testExecutions,
        testCaseKey,
      }
    : null;
}

function convertToString(pageNumber, filters?: { [key: string]: string }) {
  let params = `page=${pageNumber}`;
  if (filters) {
    Object.keys(filters).forEach((key) => {
      if (key === 'projectId') {
        if (filters[key] === 'ALL_PROJECTS') {
          throw new Error('It is not possible to filter by all projects!');
        }
      }
      const paramValue =
        key === 'search' ? encodeURIComponent(filters[key]) : filters[key];
      params += `&${key}=${paramValue}`;
    });
  }
  return params;
}
